import React, { useState, useRef, useEffect } from 'react';

import { localPoint } from '@visx/event';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';

import { TooltipInPortalProps } from '@visx/tooltip/lib/hooks/useTooltipInPortal';

import { OTHER_LABEL, OTHER_VALUE } from '../../constants';

export interface UseChartTooltipResponse<T> {
  TooltipInPortal: React.FC<TooltipInPortalProps>;
  containerRef: (element: SVGElement | HTMLElement | null) => void;
  handleLegendItemMouseOut: () => void;
  handleLegendItemMouseOver: (datum: T) => void;
  handleMouseEnterSvg: (event: React.MouseEvent<SVGElement>, datum: T) => void;
  handleMouseOutSvg: () => void;
  handleMouseOverSvg: (event: React.MouseEvent<SVGElement>, datum: T) => void;
  hoveredLegendItem: T | null;
  popoverProps: {
    anchorEl: SVGElement | null;
    open: boolean;
  };
  tooltipData: T | null;
  tooltipProps: { left: number; open: boolean; top: number };
}

export const useChartTooltip = <T extends { color?: string; name: string }>({
  anchorOn = 'move',
}: {
  anchorOn?: 'move' | 'enter';
}): UseChartTooltipResponse<T> => {
  const [hoveredLegendItem, setHoveredLegendItem] = useState<T | null>(null);
  const [anchorEl, setAnchorEl] = useState<SVGElement | null>(null);
  const mouseOutTimeoutRef = useRef<number | null>(null);
  const [tooltipData, setTooltipData] = useState<T | null>(null);

  const { hideTooltip, showTooltip, tooltipLeft, tooltipTop } = useTooltip<T>();
  const { TooltipInPortal, containerRef } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  useEffect(() => {
    return () => {
      if (mouseOutTimeoutRef.current) {
        clearTimeout(mouseOutTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseOverSvg = (event: React.MouseEvent<SVGElement>, datum: T) => {
    // SVG elements will have ownerSVGElement property
    const target = event.target as SVGElement;
    const svgElement = target.ownerSVGElement;
    if (!svgElement) return;

    if (anchorOn === 'move') {
      setAnchorEl(svgElement);
    }

    const coords = localPoint(target, event);

    if (coords) {
      showTooltip({
        tooltipLeft: coords.x,
        tooltipTop: coords.y,
      });
    }

    setTooltipData(datum);
    setHoveredLegendItem(datum);
  };

  const handleMouseEnterSvg = (event: React.MouseEvent<SVGElement>, datum: T) => {
    if (anchorOn === 'enter') {
      setAnchorEl(event.currentTarget);
    }

    setHoveredLegendItem(datum);
    setTooltipData(datum);
  };

  const handleMouseOutSvg = () => {
    hideTooltip();
    setHoveredLegendItem(null);
    setTooltipData(null);
  };

  const handleLegendItemMouseOver = (datum: T) => {
    if (mouseOutTimeoutRef.current) {
      clearTimeout(mouseOutTimeoutRef.current);
    }

    setHoveredLegendItem(datum);
  };

  const handleLegendItemMouseOut = () => {
    mouseOutTimeoutRef.current = window.setTimeout(() => {
      setHoveredLegendItem(null);
    }, 150);
  };

  return {
    TooltipInPortal,
    containerRef,
    handleLegendItemMouseOut,
    handleLegendItemMouseOver,
    handleMouseEnterSvg,
    handleMouseOutSvg,
    handleMouseOverSvg,
    hoveredLegendItem,
    popoverProps: {
      anchorEl,
      open: Boolean(tooltipData && [OTHER_LABEL, OTHER_VALUE].includes(tooltipData.name)),
    },
    tooltipData,
    tooltipProps: {
      left: tooltipLeft ?? 0,
      open: Boolean(tooltipData && ![OTHER_LABEL, OTHER_VALUE].includes(tooltipData.name)),
      top: tooltipTop ?? 0,
    },
  };
};
